import { Menu, MonitorSmartphone, Search } from 'lucide-react'
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import logoWhite from '@/assets/brand/Logo_White.png'
import { useAuth } from '@/auth/useAuth'
import { ROLE_LABELS, type UserRole, useMe } from '@/auth/useMe'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageNote } from '@/components/PageNote'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { NotificationsProvider } from '@/features/notifications/NotificationsProvider'
import { useCommandSearch } from '@/features/search/searchContext'
import { MODULE_LABELS, NAV_LABELS } from '@/lib/labels'
import { moduleOf, rememberPath } from '@/lib/lastModule'
import { searchShortcutLabel } from '@/lib/platform'
import { cn } from '@/lib/utils'
import { AccountMenu } from './AccountMenu'
import type { NavItem, NavSection } from './navTypes'
import { SectionSwitcher } from './SectionSwitcher'
import { useRouteFocus } from './useRouteFocus'

/** The same rule `NavLink` applies, plus the item's extra `match` prefixes. */
function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.external) return false
  const under = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  const own = item.end ? pathname === item.to : under(item.to)
  return own || (item.match ?? []).some(under)
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon
  const { pathname } = useLocation()
  const matchesExtra = (item.match ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
  // An external target leaves the app entirely, so it never gets an active
  // state and the router must not try to resolve it as a route.
  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        className="vonk-navitem text-sm"
      >
        <Icon className="size-[18px] shrink-0" />
        {item.label}
      </a>
    )
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn('vonk-navitem text-sm', (isActive || matchesExtra) && 'vonk-navitem--active')
      }
    >
      <Icon className="size-[18px] shrink-0" />
      {item.label}
      {item.desktopOnly ? (
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground md:hidden">
          <MonitorSmartphone className="size-3.5" aria-hidden />
          Groter scherm
        </span>
      ) : null}
    </NavLink>
  )
}

/** The nav body, rendered twice: once in the desktop sidebar, once in the
 *  mobile drawer. Both read the same props, so they cannot drift apart. */
function NavBody({
  navSections,
  footerItem,
  role,
  userBlock,
  onNavigate,
}: {
  navSections: readonly NavSection[]
  footerItem?: NavItem
  role: UserRole | null
  userBlock: ReactNode
  /** Only the drawer copy passes this: dismiss the panel on a nav tap. */
  onNavigate?: () => void
}) {
  return (
    <>
      <SectionSwitcher onNavigate={onNavigate} />
      <nav className="flex flex-col gap-0.5">
        {navSections.map((section) => (
          <div key={section.title} className="flex flex-col gap-0.5">
            <p className="vonk-nav-section">{section.title}</p>
            {section.items
              .filter((item) => !item.adminOnly || role === 'admin')
              .map((item) => (
                <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
              ))}
          </div>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-2">
        {footerItem ? <NavItemLink item={footerItem} onNavigate={onNavigate} /> : null}
        {userBlock}
      </div>
    </>
  )
}

/** Ties the hamburger's `aria-controls` to the drawer panel. Passing it as an
 *  `id` overrides the one Radix generates, which is only read by `SheetTrigger`
 *  — unused here, since the drawer opens programmatically. */
const DRAWER_ID = 'workspace-nav-drawer'

/**
 * The one workspace shell. At `md` and up this renders exactly what the four
 * per-feature layouts rendered before: a sticky `w-60` sidebar plus the routed
 * content. Below `md` the sidebar is replaced by a topbar with a hamburger that
 * opens the same nav in a sheet. UX-only role gating — the backend enforces RBAC.
 */
export function WorkspaceLayout({
  navSections,
  footerItem,
}: {
  navSections: readonly NavSection[]
  footerItem?: NavItem
}) {
  const { me, loading } = useMe()
  const { signOut } = useAuth()
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  useRouteFocus(mainRef)
  // From md up <main> is the scroll container (the shell is one window high), so a new
  // page starts at the top. Keyed on the pathname only: a tab switch inside a dossier
  // (search params) keeps its position under the sticky bar.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [location.pathname])
  const isBoard = location.pathname === '/preinvest/board'
  /**
   * The drawer's open state is *derived* from the route: we remember the
   * pathname it was opened at and treat it as open only while that is still the
   * current pathname. A browser/Android back gesture changes the route without
   * firing any of our handlers, and this closes the sheet for free — no effect
   * keyed on `location.pathname` (banned by `react-hooks/set-state-in-effect`,
   * and it would not cover the same-route case anyway).
   */
  const [openedAtPath, setOpenedAtPath] = useState<string | null>(null)
  const drawerOpen = openedAtPath !== null && openedAtPath === location.pathname
  const closeDrawer = () => setOpenedAtPath(null)
  const role = me?.role ?? null
  // Written to storage only; the section switcher reads it on click.
  useEffect(() => {
    rememberPath(location.pathname + location.search)
  }, [location.pathname, location.search])
  const moduleKey = moduleOf(location.pathname)
  const activeItem = [...navSections.flatMap((section) => section.items), footerItem].find(
    (item): item is NavItem => !!item && isItemActive(item, location.pathname),
  )
  const pageTitle = activeItem?.label ?? (moduleKey ? MODULE_LABELS[moduleKey] : 'VONK')
  const allowed = role === 'admin' || role === 'team' || role === 'fundraiser'
  // spec-62: the ⌘K palette lives in CommandSearchHost; these buttons open it.
  const search = useCommandSearch()
  const shortcut = searchShortcutLabel()

  /**
   * `withBell: false` is for the drawer copy, which lists the account entries as
   * plain items. The bell itself is not in this block: on desktop it sits next to
   * the logo at the top of the sidebar, on a phone in the topbar.
   */
  const renderUserBlock = (withBell: boolean) =>
    me ? (
      <div className="flex items-center justify-between gap-2 border-t border-sidebar-border pt-3">
        {withBell ? (
          <div className="min-w-0 flex-1">
            <AccountMenu
              name={me.full_name}
              roleLabel={ROLE_LABELS[me.role]}
              onSignOut={() => void signOut()}
            />
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <AccountMenu
              name={me.full_name}
              roleLabel={ROLE_LABELS[me.role]}
              onSignOut={() => void signOut()}
              variant="list"
              onNavigate={closeDrawer}
            />
          </div>
        )}
      </div>
    ) : null

  const userBlock = renderUserBlock(true)
  const drawerUserBlock = renderUserBlock(false)

  /** `onNavigate` is only passed by the drawer copy: the logo navigates to `/`,
   *  which must dismiss the panel just like a nav item does. */
  const renderBrand = (onNavigate?: () => void) => (
    <Link
      to="/"
      onClick={onNavigate}
      aria-label={NAV_LABELS.home}
      title={NAV_LABELS.home}
      className="mb-2 self-start rounded-md transition-opacity hover:opacity-90"
    >
      <img src={logoWhite} alt="VONK Ventures" className="h-9 w-auto" />
    </Link>
  )

  return (
    <NotificationsProvider recipientId={me?.id ?? null}>
      <div className="vonk-workspace flex min-h-svh flex-col md:h-svh md:flex-row md:overflow-hidden">
        <a
          href="#inhoud"
          // Lands the focus on the page heading, like a route change does; the href stays for
          // browsers without script.
          onClick={(event) => {
            const main = mainRef.current
            if (!main) return
            event.preventDefault()
            const heading = main.querySelector<HTMLElement>('h1')
            const target = heading ?? main
            if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
            target.focus()
          }}
          className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-foreground focus:shadow-lg"
        >
          Naar inhoud
        </a>
        <aside className="vonk-sidebar sticky top-0 hidden h-svh w-60 shrink-0 flex-col gap-2 p-4 md:flex">
          <div className="flex items-start justify-between gap-2">
            {renderBrand()}
            {me ? <NotificationBell /> : null}
          </div>
          {search.available ? (
            <button
              type="button"
              title={`Zoeken (${shortcut})`}
              onClick={search.open}
              className="mb-2 flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-left text-sm text-sidebar-label transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <Search aria-hidden className="size-4 shrink-0" />
              <span className="flex-1">Zoeken</span>
              <kbd className="rounded border border-sidebar-border px-1.5 text-xs">
                {shortcut}
              </kbd>
            </button>
          ) : null}
          <NavBody
            navSections={navSections}
            footerItem={footerItem}
            role={role}
            userBlock={userBlock}
          />
        </aside>

        {/* Three columns with equal sides keep the logo centred: the right side
            (search + bell) is wider than the menu button, and it grows once `me`
            has loaded. */}
        <header className="vonk-sidebar sticky top-0 z-40 grid h-14 grid-cols-[auto_1fr_auto] items-center gap-2 px-3 md:hidden">
          <button
            type="button"
            aria-label="Menu openen"
            // The sheet is opened programmatically instead of through
            // `SheetTrigger`, so Radix wires up none of these for us.
            // `aria-controls` is dropped while closed because the panel is
            // portalled on open: pointing at an absent id is worse than omitting
            // it (this mirrors what `SheetTrigger` itself does).
            aria-expanded={drawerOpen}
            aria-haspopup="dialog"
            aria-controls={drawerOpen ? DRAWER_ID : undefined}
            onClick={() => setOpenedAtPath(location.pathname)}
            className="justify-self-start rounded-md p-3 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            <Menu className="size-6" />
          </button>
          <p className="truncate text-center text-base font-semibold text-sidebar-foreground">
            {pageTitle}
          </p>
          <div className="flex items-center gap-1 justify-self-end">
            {search.available ? (
              <button
                type="button"
                aria-label="Zoeken"
                onClick={search.open}
                className="rounded-md p-3 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                {/* 18px like the bell next to it. */}
                <Search className="size-[18px]" />
              </button>
            ) : null}
            {me ? <NotificationBell /> : <span className="w-11" />}
          </div>
        </header>

        {/* `md:hidden` on both the panel and the scrim hides the drawer *visually*
            above `md`, but the modal behaviour survives the breakpoint: Radix
            keeps `body { pointer-events: none }`, the scroll lock and the focus
            trap in place. Cross `md` with the drawer open (tablet rotation,
            window resize) and you get a desktop page that cannot be clicked or
            scrolled, because the affordance that would close it is now invisible.
            Recovery is Escape, or crossing back under `md`, which makes the scrim
            tappable again — so a touch-only device is not stuck. Accepted:
            undoing it needs either JS width sensing (this layout is deliberately
            CSS-only) or unmounting the sheet, which CSS cannot express. */}
        <Sheet
          open={drawerOpen}
          onOpenChange={(next) => setOpenedAtPath(next ? location.pathname : null)}
        >
          <SheetContent
            id={DRAWER_ID}
            side="left"
            className="vonk-sidebar md:hidden"
            overlayClassName="md:hidden"
          >
            <SheetTitle className="sr-only">Navigatie</SheetTitle>
            {renderBrand(closeDrawer)}
            {/* A route change closes the drawer on its own (the open state is
                derived from the pathname). `onNavigate` covers the one case
                that leaves the pathname untouched: tapping the entry for the
                page you are already on. */}
            <NavBody
              navSections={navSections}
              footerItem={footerItem}
              role={role}
              userBlock={drawerUserBlock}
              onNavigate={closeDrawer}
            />
          </SheetContent>
        </Sheet>

        {/* spec-65 U-029: room under the last line so the floating Sparky button
            never covers it. The deal board sizes itself to the viewport from md
            up and scrolls per lane there, so it only needs the room below md,
            where its lanes stack and scroll inside <main>. */}
        <main
          id="inhoud"
          ref={mainRef}
          tabIndex={-1}
          className={cn(
            'flex-1 overflow-auto p-4 md:min-h-0 md:p-6',
            isBoard ? 'max-md:pb-24 md:flex md:flex-col' : 'pb-24 md:pb-24',
          )}
        >
          {/* The sidebar stays mounted across navigations; only this content
              area swaps. The loader/gate render here (not full-page) so the red
              shell never disappears, and the fade keeps route changes from
              feeling shaky. The key restarts the fade + resets the boundary per
              route. */}
          <div
            key={location.pathname}
            className={cn('vonk-page-fade', isBoard && 'md:flex md:min-h-0 md:flex-1 md:flex-col')}
          >
            {loading ? (
              <PageNote>Bezig met laden…</PageNote>
            ) : !allowed ? (
              <PageNote>Je hebt geen toegang tot dit onderdeel.</PageNote>
            ) : (
              // Wrap only the page content: a crash here keeps the sidebar nav
              // usable so the user can navigate out.
              <ErrorBoundary>
                <Suspense fallback={<PageNote>Bezig met laden…</PageNote>}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
        </main>
      </div>
    </NotificationsProvider>
  )
}
